import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle2, Loader2 } from "lucide-react";
import { es } from "date-fns/locale";
import { API } from "@/config";
import ServicePicker from "@/components/site/ServicePicker";

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingModal({ open, initialService, onClose }) {
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState(null);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("estudio");
  const [address, setAddress] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) {
      setItems(initialService ? [{ name: initialService, qty: 1 }] : []);
      setDate(null);
      setTime("");
      setAvail(null);
      setDone(null);
      axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
      axios.get(`${API}/schedule`).then((r) => setSchedule(r.data)).catch(() => {});
    }
  }, [open, initialService]);

  useEffect(() => {
    if (date) {
      setTime("");
      setAvail(null);
      axios
        .get(`${API}/appointments/availability?date=${toKey(date)}`)
        .then((r) => setAvail(r.data))
        .catch(() => toast.error("No se pudo comprobar la disponibilidad"));
    }
  }, [date]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        location,
        address: address.trim(),
        items,
        date: toKey(date),
        time,
      });
      setDone({ service: items.map((i) => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ""}`).join(" + "), date, time });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reservar, inténtalo de nuevo");
    } finally {
      setSending(false);
    }
  };

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const ready = items.length > 0 && date && time && name.trim() && phone.trim() && emailOk && (location === "estudio" || address.trim().length >= 5);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="booking-modal"
        data-lenis-prevent
        className="bg-creamcard border-sand max-w-xl max-h-[90vh] overflow-y-auto"
      >
        {done ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian mt-5">
              ¡Cita solicitada!
            </h3>
            <p className="text-sm sm:text-base text-taupe mt-3 leading-relaxed">
              {done.service} ·{" "}
              {done.date.toLocaleDateString("es-ES", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}{" "}
              a las {done.time}
            </p>
            <p className="text-sm text-taupe mt-2">
              Te acabamos de enviar un correo con los datos de tu solicitud. Isis la revisará y recibirás la confirmación también por correo.
              Si no lo ves, mira en tu carpeta de spam o correo no deseado.
            </p>
            <button
              onClick={onClose}
              data-testid="booking-done-close-btn"
              className="mt-6 rounded-full bg-gold hover:bg-gold-dark text-white text-sm font-medium px-8 py-3 transition-colors"
            >
              Perfecto
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-obsidian">Agenda tu cita</DialogTitle>
              <DialogDescription className="text-taupe">
                Elige servicio, día y hora. Isis te contactará para confirmar.
              </DialogDescription>
            </DialogHeader>

            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">1 · Servicios</p>
              <ServicePicker services={services} items={items} onChange={setItems} testPrefix="booking" />
            </div>

            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">2 · Día</p>
              <div className="mt-3 flex justify-center rounded-xl border border-sand bg-cream">
                <Calendar
                  mode="single"
                  locale={es}
                  selected={date}
                  onSelect={setDate}
                  disabled={[
                    { before: today },
                    (d) => (schedule ? !schedule.days.includes(d.getDay()) : d.getDay() === 0),
                    (d) => Boolean(schedule?.blocked?.includes(toKey(d))),
                  ]}
                  data-testid="booking-calendar"
                />
              </div>
            </div>

            {date && (
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">3 · Hora</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                  {(avail?.slots || []).map((s) => {
                    const taken = avail.taken.includes(s) || (avail.blocked || []).includes(s);
                    return (
                      <button
                        key={s}
                        disabled={taken}
                        onClick={() => setTime(s)}
                        data-testid={`booking-slot-${s.replace(":", "")}`}
                        className={`rounded-lg border px-2 py-2 text-sm transition-colors duration-200 ${
                          taken
                            ? "border-sand bg-sand/40 text-taupe/50 line-through cursor-not-allowed"
                            : time === s
                              ? "border-gold bg-gold text-white"
                              : "border-sand bg-cream text-cocoa hover:border-gold/60"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                  {!avail && <Loader2 className="w-5 h-5 text-gold animate-spin col-span-full mx-auto" />}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">4 · Tus datos</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  data-testid="booking-name-input"
                  className="rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Tu teléfono"
                  data-testid="booking-phone-input"
                  className="rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  data-testid="booking-email-input"
                  className="sm:col-span-2 rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <p className="text-xs text-taupe mt-2">
                Te enviaremos un correo al reservar, la confirmación cuando Isis la acepte y un recordatorio el día anterior. Revisa tu carpeta de spam si no los ves.
              </p>
            </div>

            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">5 · ¿Dónde?</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {[
                  { id: "estudio", label: "En el estudio de Isis", sub: "Yo voy a su local" },
                  { id: "domicilio", label: "En mi domicilio", sub: "Isis viene a mi casa" },
                ].map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setLocation(o.id)}
                    data-testid={`booking-location-${o.id}`}
                    className={`rounded-xl border px-3 py-2.5 text-left text-sm transition-colors duration-200 ${
                      location === o.id ? "border-gold bg-rosesoft text-obsidian" : "border-sand bg-cream text-cocoa hover:border-gold/50"
                    }`}
                  >
                    <span className="font-medium block">{o.label}</span>
                    <span className="text-xs text-taupe">{o.sub}</span>
                  </button>
                ))}
              </div>
              {location === "domicilio" && (
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Dirección completa: calle, número, piso, ciudad y código postal"
                  rows={2}
                  data-testid="booking-address-input"
                  className="mt-3 w-full rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
                />
              )}
            </div>

            <button
              onClick={submit}
              disabled={!ready || sending}
              data-testid="booking-submit-btn"
              className="w-full rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium py-3.5 transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              Solicitar cita
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
